import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../lib/trpc.js";
import { Card, ErrorBox, PageHeader, Spinner } from "../components/ui.js";
import { recordLabel } from "../lib/format.js";

export default function Managers() {
  const trpc = useTRPC();
  const managers = useQuery(trpc.league.managers.queryOptions());
  // All-time records come from the leaderboard endpoint, which already
  // aggregates every finished game across every season.
  const allTime = useQuery(trpc.league.leaderboards.queryOptions({ allTime: true }));

  const recordOf = new Map(
    (allTime.data?.entries ?? []).map((e) => [e.managerId, e]),
  );

  return (
    <div>
      <PageHeader
        title="Managers"
        subtitle="Everyone who has driven a team in the league."
      />

      {managers.isLoading && <Spinner />}
      {managers.error && <ErrorBox error={managers.error} />}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {managers.data?.map((m) => {
          const rec = recordOf.get(m.id);
          return (
            <Link key={m.id} to={`/managers/${m.slug}`} className="block">
              <Card className="h-full transition hover:border-field-600">
                <div className="p-4">
                  <div className="flex items-baseline justify-between gap-2">
                    <h2 className="text-lg font-bold">{m.name}</h2>
                    {m.role === "commissioner" && (
                      <span className="chip text-[10px]">Commissioner</span>
                    )}
                  </div>
                  <p className="mt-1 font-mono text-sm text-chalk-400">
                    {rec
                      ? `${recordLabel(rec.wins, rec.losses)} all time · ${rec.games} games`
                      : "No games yet"}
                  </p>
                  {m.bio && (
                    <p className="mt-2 line-clamp-2 text-xs text-chalk-500">
                      {m.bio}
                    </p>
                  )}
                  {rec && rec.gamesWithStats > 0 && (
                    <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-field-800 pt-3 text-center">
                      <div>
                        <dt className="text-[10px] uppercase text-chalk-600">
                          Pass yds
                        </dt>
                        <dd className="font-mono text-sm">
                          {rec.totals.pass_yds ?? 0}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[10px] uppercase text-chalk-600">
                          Rush yds
                        </dt>
                        <dd className="font-mono text-sm">{rec.derived.rushYds}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] uppercase text-chalk-600">
                          TO margin
                        </dt>
                        <dd className="font-mono text-sm">
                          {rec.turnoverMargin > 0 ? "+" : ""}
                          {rec.turnoverMargin}
                        </dd>
                      </div>
                    </dl>
                  )}
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
